import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('asa_token');
    if (!token) { setLoading(false); return; }
    api.get('/me')
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem('asa_token'))
      .finally(() => setLoading(false));
  }, []);

  const login = async (userId, code) => {
    const r = await api.post('/auth/login', { user_id: userId, code });
    localStorage.setItem('asa_token', r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  // Iter47c — code-only auto-detect login
  const loginByCode = async (code) => {
    const r = await api.post('/auth/login-by-code', { code });
    localStorage.setItem('asa_token', r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  // Iter47n — Classic name+password login for dynamic (approved) users
  const loginClassic = async (first_name, last_name, password) => {
    const r = await api.post('/auth/login-classic', { first_name, last_name, password });
    localStorage.setItem('asa_token', r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  // Iter47n — Self-signup: creates a pending user
  const signup = async (first_name, last_name, email, password) => {
    const r = await api.post('/auth/signup', { first_name, last_name, email, password });
    return r.data;
  };

  // Iter 62cn — Founder impersonation
  const impersonate = async (userId) => {
    const currentToken = localStorage.getItem('asa_token');
    const r = await api.post(`/admin/impersonate/${userId}`);
    if (currentToken) localStorage.setItem('asa_impersonate_from', currentToken);
    localStorage.setItem('asa_token', r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const stopImpersonate = () => {
    const original = localStorage.getItem('asa_impersonate_from');
    if (!original) return;
    localStorage.setItem('asa_token', original);
    localStorage.removeItem('asa_impersonate_from');
    window.location.href = '/';
  };
  const isImpersonating = typeof window !== 'undefined' && !!localStorage.getItem('asa_impersonate_from');

  // Iter 62fh — Set session directly (used by guest magic-link login flow)
  const setSession = (jwt, u) => {
    localStorage.setItem('asa_token', jwt);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem('asa_token');
    localStorage.removeItem('asa_guest_link');
    localStorage.removeItem('asa_impersonate_from');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginByCode, loginClassic, signup, logout, setSession,
      impersonate, stopImpersonate, isImpersonating,
      // Iter 62bp — 'gestionnaire' role behaves like founder for most UI checks.
      // Use isFounderStrict to gate features reserved to the actual founders.
      isFounder: user?.role === 'founder' || user?.role === 'gestionnaire',
      isFounderStrict: user?.role === 'founder',
      isManager: user?.role === 'gestionnaire',
      isGuest: user?.role === 'guest',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
